import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { MapPin, Mail, Phone, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Single source of truth for the business NAP (name, address, phone). Keep this
// identical to the details published on the other network sites — inconsistent
// NAP is what weakens local search results.
const LOCAL_BUSINESS_LD = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://paisley.org.uk/contact#business',
  name: 'Paisley.org.uk',
  description:
    'Local news, events, history and community guide for Paisley, Renfrewshire, Scotland.',
  url: 'https://paisley.org.uk',
  logo: 'https://paisley.org.uk/logo512.png',
  image: 'https://paisley.org.uk/og-paisley-abbey.jpg',
  email: 'brian@paisley.org.uk',
  telephone: '+44 7957 166339',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '14 High Street',
    addressLocality: 'Paisley',
    addressRegion: 'Renfrewshire',
    postalCode: 'PA1 2BS',
    addressCountry: 'GB',
  },
  geo: { '@type': 'GeoCoordinates', latitude: 55.8456, longitude: -4.4239 },
  areaServed: { '@type': 'Place', name: 'Paisley, Renfrewshire, Scotland' },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  ],
  sameAs: [
    'https://www.facebook.com/paisleyorguk',
    'https://twitter.com/paisleyorguk',
    'https://www.instagram.com/paisleyscotland',
  ],
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    newsletter: false
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSent(true);
      toast({ title: "Message Sent!", description: "We'll get back to you as soon as possible." });
      setFormData({ name: '', email: '', subject: '', message: '', newsletter: false });
    } catch (error) {
      toast({ title: "Error", description: "Failed to send message. Please try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact Us | Paisley.org.uk"
        description="Contact Paisley.org.uk at 14 High Street, Paisley PA1 2BS. Call 07957 166339 or email brian@paisley.org.uk about news, events and sponsorship."
        keywords="Contact Paisley, Paisley Scotland contact, Paisley.org.uk"
        url="/contact"
        jsonLd={LOCAL_BUSINESS_LD}
      />
      <Header />
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4" data-testid="contact-page-title">
            Get in <span className="text-cyan-400">Touch</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Have a question, story tip, or want to advertise? We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">

            {/* Contact Form */}
            <div className="lg:col-span-3">
              {sent ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-12 text-center" data-testid="contact-success">
                  <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
                  <h2 className="text-2xl font-bold text-green-900 mb-2">Message Sent!</h2>
                  <p className="text-green-700 mb-6">Thank you for getting in touch. We'll get back to you as soon as possible.</p>
                  <Button onClick={() => setSent(false)} variant="outline" data-testid="send-another-btn">
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a message</h2>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium">Name *</Label>
                      <Input
                        id="name"
                        data-testid="contact-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        required
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-700 font-medium">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        required
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-slate-700 font-medium">Subject</Label>
                    <Input
                      id="subject"
                      data-testid="contact-subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="What's this about?"
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700 font-medium">Message *</Label>
                    <textarea
                      id="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Your message..."
                      required
                      rows={6}
                      className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-vertical"
                    />
                  </div>

                  {/* Newsletter Checkbox */}
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="newsletter-section">
                    <input
                      type="checkbox"
                      id="newsletter"
                      data-testid="newsletter-checkbox"
                      checked={formData.newsletter}
                      onChange={(e) => setFormData({ ...formData, newsletter: e.target.checked })}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                    />
                    <label htmlFor="newsletter" className="cursor-pointer">
                      <span className="text-sm font-medium text-slate-900 block">Subscribe to our Newsletter</span>
                      <span className="text-xs text-gray-500">Get the latest Paisley news, events and updates delivered to your inbox. You can unsubscribe at any time.</span>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={sending}
                    data-testid="contact-submit-btn"
                    className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-base w-full sm:w-auto"
                  >
                    {sending ? (
                      <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span> Sending...</>
                    ) : (
                      <><Send className="mr-2" size={18} /> Send Message</>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Info</h2>

              <div className="space-y-5">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Address</h3>
                    <p className="text-gray-600 text-sm" data-testid="contact-address">
                      Paisley.org.uk<br />
                      14 High Street<br />
                      Paisley<br />
                      Renfrewshire PA1 2BS<br />
                      Scotland
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Phone</h3>
                    <a href="tel:+447957166339" className="text-cyan-600 hover:text-cyan-700 text-sm"
                       data-testid="contact-phone">
                      07957 166339
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Email</h3>
                    <a href="mailto:brian@paisley.org.uk" className="text-cyan-600 hover:text-cyan-700 text-sm">brian@paisley.org.uk</a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Response Time</h3>
                    <p className="text-gray-600 text-sm">We aim to respond within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-testid="paisley-map">
                <iframe
                  title="Map of Paisley, Scotland"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d17892.14!2d-4.4309!3d55.8456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x488847a3b4aaaab1%3A0x1d4a64ee5ab0dcca!2sPaisley!5e0!3m2!1sen!2suk!4v1711630000000!5m2!1sen!2suk"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
